import {
  AccountSummary,
  AccountTradingPlanStatusEnum,
  DetailedAccount,
  PlanPhaseTypeEnum,
  ReferralPayoutRequest,
  ReferralProfile,
  ReferralProfileResponse,
  TradingAccountStatusesEnum,
  TradingPlan,
} from '../../models/crm/types';
import { AccountCreatedEvent } from '../../services/user-events/sse-event-types';
import { NullableNumber } from '../../util/types';

/**
 * The responses of the CRM calls carry far more than the app reads. The functions below keep only
 * the properties which are actually used, so the store holds no unused data.
 */

export const toDetailedAccount = ({
  account_expiration_date,
  account_id,
  account_status,
  account_trading_plan_status,
  cash_account_reference_id,
  is_cash_account,
  reference_id,
  trading_plan_id,
}: AccountSummary): DetailedAccount => ({
  account_expiration_date,
  account_id,
  account_status,
  account_trading_plan_status,
  cash_account_reference_id,
  is_cash_account,
  reference_id,
  trading_plan_id,
});


export const toTradingPlan = ({
  auto_pass_deactivation_ccy,
  auto_pass_deactivation_price,
  consistency_coefficient,
  consistency_coefficient_minimum,
  daily_hard_bp,
  daily_hard_ccy,
  daily_loss_limit,
  daily_loss_limit_amount,
  id,
  maximum_total_loss,
  maximum_total_loss_amount,
  min_trading_days_target,
  name,
  next_trading_plan_id,
  no_gambling_rule,
  payout_split,
  penalty_payout_split,
  phase,
  plan_price,
  plan_price_ccy,
  plan_price_with_auto_pass,
  plan_type,
  plans_accounts_activation,
  previous_trading_plan_id,
  profit_target,
  profit_target_amount,
  reference_id,
  rewind_daily_loss_price,
  rewind_daily_loss_price_ccy,
  trading_plan_status_enum,
}: TradingPlan): TradingPlan => ({
  auto_pass_deactivation_ccy,
  auto_pass_deactivation_price,
  consistency_coefficient,
  consistency_coefficient_minimum,
  daily_hard_bp,
  daily_hard_ccy,
  daily_loss_limit,
  daily_loss_limit_amount,
  id,
  maximum_total_loss,
  maximum_total_loss_amount,
  min_trading_days_target,
  name,
  next_trading_plan_id,
  no_gambling_rule,
  payout_split,
  penalty_payout_split,
  phase: phase === PlanPhaseTypeEnum.theChallenge ? PlanPhaseTypeEnum.challenge : phase,
  plan_price,
  plan_price_ccy,
  plan_price_with_auto_pass,
  plan_type,
  plans_accounts_activation,
  previous_trading_plan_id,
  profit_target,
  profit_target_amount,
  reference_id,
  rewind_daily_loss_price,
  rewind_daily_loss_price_ccy,
  trading_plan_status_enum,
});

/**
 * The account an `AccountCreated` event describes, so the plan can be rendered while the account is
 * still being created.
 *
 * The event carries no status, so the caller derives it - `PendingCreation` for every account
 * except the user's first ever `Entry Funded` one, which starts `ForApproval`. It is passed in
 * rather than assumed here, because answering it needs the trading plans and the account history.
 * The expiration date is filled in by the accounts call once the account is usable.
 * Cash account events are filtered out before this, so the account is always a trading one.
 */
export const toDetailedAccountFromEvent = (
  { data }: AccountCreatedEvent,
  accountStatus: TradingAccountStatusesEnum,
): DetailedAccount => ({
  account_expiration_date: null,
  account_id: data.accountId,
  account_status: accountStatus,
  account_trading_plan_status: AccountTradingPlanStatusEnum.Unspecified,
  cash_account_reference_id: null,
  is_cash_account: false,
  reference_id: data.accountReferenceId,
  trading_plan_id: data.tradingPlanId,
});

export const toReferralProfile = ({
  balances,
  can_invite,
  can_request_payout,
  code,
  min_payout_amount,
  referral_link,
  referred_purchased_count,
  referred_registered_count,
}: ReferralProfileResponse): ReferralProfile => ({
  balances,
  can_invite,
  can_request_payout,
  code,
  min_payout_amount,
  referral_link,
  referred_purchased_count,
  referred_registered_count,
});

export const toRequestedPayoutAmount = (payoutRequest: ReferralPayoutRequest): NullableNumber => {
  const [total] = payoutRequest?.totals ?? [];

  return total?.amount ?? null;
};
